import React, { useState } from "react";
import { ApprovalData } from "../types/chat";
import ReactMarkdown from "react-markdown";
import "./InlineApproval.css";

interface InlineApprovalProps {
  approvalData: ApprovalData;
  onApprove: () => void;
  onReject: () => void;
}

const getIcon = (tool: string): string => {
  const icons: { [key: string]: string } = {
    'call_erp_api': '🔍',
    'generate_plan': '📄',
    'send_email': '✉️',
    'create_document': '📝',
    'update_record': '✏️',
    'delete_record': '🗑️',
  };
  return icons[tool] || '⚙️';
};

const InlineApproval: React.FC<InlineApprovalProps> = ({
  approvalData,
  onApprove,
  onReject,
}) => {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const formatDataForDisplay = (data: any): string => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  // Determine which fields to display
  const displayTitle = approvalData.action_description || 'API Approval Required';
  const displayMessage = approvalData.user_friendly_summary || approvalData.message || 'The AI assistant is requesting permission to execute an action';

  const handleApprove = () => {
    setIsProcessing(true);
    onApprove();
  };

  const handleReject = () => {
    setIsProcessing(true);
    onReject();
  };

  return (
    <div className="inline-approval">
      <div className="inline-approval-header">
        <span className="inline-approval-icon">{getIcon(approvalData.tool)}</span>
        <h4 className="inline-approval-title">{displayTitle}</h4>
      </div>

      <div className="inline-approval-body">
        <p className="inline-approval-message">{displayMessage}</p>

        {!isProcessing ? (
          <div className="inline-approval-actions">
            <button
              type="button"
              className="inline-approval-btn inline-approval-btn-reject"
              onClick={handleReject}
            >
              ❌ Reject
            </button>
            <button
              type="button"
              className="inline-approval-btn inline-approval-btn-approve"
              onClick={handleApprove}
            >
              ✅ Approve
            </button>
          </div>
        ) : (
          <div className="inline-approval-processing">
            <span className="processing-spinner"></span>
            <span>Processing...</span>
          </div>
        )}

        {/* Collapsible technical details - Commented out for now */}
        {/* <div className="inline-approval-details">
          <button
            className="inline-approval-details-toggle"
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            type="button"
          >
            <span className={`toggle-icon ${showTechnicalDetails ? 'open' : ''}`}>▼</span>
            {showTechnicalDetails ? 'Hide' : 'Show'} technical details
          </button>

          {showTechnicalDetails && (
            <div className="inline-approval-details-content">
              <div className="inline-approval-section">
                <h5 className="inline-approval-section-title">Action Summary</h5>
                <div className="inline-approval-info-grid">
                  <div className="inline-approval-info-item">
                    <span className="inline-approval-label">Tool:</span>
                    <span className="inline-approval-value">{approvalData.tool}</span>
                  </div>
                  <div className="inline-approval-info-item">
                    <span className="inline-approval-label">Method:</span>
                    <span className={`inline-approval-badge method-${approvalData.action?.toLowerCase() || 'unknown'}`}>
                      {approvalData.action || 'UNKNOWN'}
                    </span>
                  </div>
                  <div className="inline-approval-info-item full-width">
                    <span className="inline-approval-label">Endpoint:</span>
                    <code className="inline-approval-endpoint">{approvalData.endpoint}</code>
                  </div>
                </div>
              </div>

              {approvalData.data && Object.keys(approvalData.data).length > 0 && (
                <div className="inline-approval-section">
                  <h5 className="inline-approval-section-title">Request Data</h5>
                  <pre className="inline-approval-data-preview">
                    {formatDataForDisplay(approvalData.data)}
                  </pre>
                </div>
              )}

              {approvalData.technical_details && (
                <div className="inline-approval-section">
                  <h5 className="inline-approval-section-title">Technical Details</h5>
                  <pre className="inline-approval-data-preview">
                    {formatDataForDisplay(approvalData.technical_details)}
                  </pre>
                </div>
              )}

              {approvalData.api_info && (
                <div className="inline-approval-section">
                  <h5 className="inline-approval-section-title">API Documentation</h5>
                  <div className="inline-approval-api-info">
                    <ReactMarkdown>{approvalData.api_info}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default InlineApproval;
